import { jsxs, jsx, Fragment } from 'react/jsx-runtime';
import { forwardRef, useState, useRef, useMemo, useEffect, Children, useCallback } from 'react';
import clsx from 'clsx';
import { IconChevronLeftSmall } from '../../../../foundation/icons/IconChevronLeftSmall/index.js';
import { IconChevronRightSmall } from '../../../../foundation/icons/IconChevronRightSmall/index.js';
import { CarouselPagination } from '../CarouselPagination/index.js';
import { ButtonPlacements, CarouselVariants } from '../types/index.js';
import { useStyles } from '../styles/index.js';
import { useFirstRender } from '../../../hooks/src/useFirstRender/index.js';
import { ButtonSkinStatusPositive } from '../../../atoms/Button/skins/index.js';
import { computeResponsiveness } from '../../../../foundation/responsive/helpers/index.js';
import { Spacings } from '../../../../foundation/spacings/spacings/index.js';
import { isDesktopDevice } from '../../../../foundation/utils/getDeviceType/getDeviceType/index.js';
import { Timings } from '../../../../foundation/timings/timings/index.js';
import { Easing } from '../../../../foundation/easing/easing/index.js';
import { Button } from '../../../atoms/Button/Button/index.js';

function mergeRefs(...refs) {
    return (value) => {
        refs.forEach(currentRef => {
            if (typeof currentRef === 'function') {
                currentRef(value);
            }
            else if (currentRef && typeof currentRef === 'object') {
                const mutableRef = currentRef;
                mutableRef.current = value;
            }
        });
    };
}
function getScrollValue(direction, scrollAmount, reverse = false) {
    const isRtl = direction === 'rtl';
    return reverse ? (isRtl ? scrollAmount : -scrollAmount) : (isRtl ? -scrollAmount : scrollAmount);
}
const Carousel = forwardRef(({ variant = CarouselVariants.Swipe, children, autoplay, duration = 1000, hidePagination, hideArrows, height, dataTestId, activeIndex = 0, isMultiSlide = false, buttonPlacement = ButtonPlacements.Overlay, dotsPlacement = ButtonPlacements.Overlay, gap = Spacings.s10, contentClassName, className, onNext, onPrevious, onChange, prevButtonProps, nextButtonProps, showGradients = true, showArrowOnHover = false, scrollAmount = 200, arrowSize, }, ref) => {
    const sizes = computeResponsiveness(height);
    const gaps = computeResponsiveness(gap);
    const [activeSlide, setActiveSlide] = useState(1);
    const contentRef = useRef(null);
    const mergedRef = useMemo(() => mergeRefs(ref, contentRef), [ref, contentRef]);
    const [touchStartX, setTouchStartX] = useState(0);
    const [touchCurrentX, setTouchCurrentX] = useState(0);
    const [isSwiping, setIsSwiping] = useState(false);
    const [isPaused, setPaused] = useState(false);
    const [activePaginationIndex, setActivePaginationIndex] = useState(activeSlide - 1);
    const [disableAutoscroll, setDisableAutoscroll] = useState(!!hideArrows);
    const { direction } = window.getComputedStyle(contentRef?.current || document.body);
    const [isMerged, setIsMerged] = useState(isMultiSlide);
    const [hideLeftArrow, setHideLeftArrow] = useState(!!isMultiSlide);
    const [hideRightArrow, setHideRightArrow] = useState(false);
    useEffect(() => {
        activeIndex && setActiveSlide(activeIndex + 1);
    }, [activeIndex]);
    useEffect(() => {
        const node = contentRef.current;
        if (node && isMultiSlide) {
            const handleScroll = () => {
                const { scrollLeft, clientWidth, scrollWidth, } = node;
                setHideLeftArrow(scrollLeft === 0);
                setHideRightArrow(direction === 'rtl' ? Math.abs(scrollLeft) + clientWidth === scrollWidth : scrollLeft + clientWidth === scrollWidth);
            };
            node.addEventListener('scroll', handleScroll);
            handleScroll();
            return () => {
                node.removeEventListener('scroll', handleScroll);
            };
        }
    }, [direction, isMultiSlide]);
    useEffect(() => {
        setIsMerged(isMultiSlide);
    }, [isMultiSlide]);
    useEffect(() => {
        onChange?.(activePaginationIndex);
    }, [activePaginationIndex, onChange]);
    const [showArrow, setShowArrow] = useState(!showArrowOnHover || !isDesktopDevice);
    const isFirstRender = useFirstRender();
    const length = useMemo(() => Children.toArray(children).length + 2, [children]);
    const styles = useStyles({
        sizes,
        isFirstRender,
        variant,
        isMultiSlide,
        buttonPlacement,
        gaps,
        showGradients,
    });
    useEffect(() => {
        if (!isFirstRender && contentRef.current) {
            setActivePaginationIndex(activeSlide === length - 1 ? 0 : activeSlide === 0 ? length - 3 : activeSlide - 1);
        }
        !isMultiSlide && contentRef.current && (contentRef.current.style.transform = `translateX(${activeSlide * (-100)}%)`);
    }, [activeSlide, isFirstRender, length, isMultiSlide]);
    useEffect(() => {
        const node = contentRef.current;
        if (node) {
            node.addEventListener('mouseenter', () => setPaused(true));
            node.addEventListener('mouseleave', () => setPaused(false));
            return () => {
                node.removeEventListener('mouseenter', () => setPaused(true));
                node.removeEventListener('mouseleave', () => setPaused(false));
            };
        }
    }, [contentRef, variant]);
    const scroll = useCallback((scrollValue) => {
        if (contentRef.current) {
            contentRef.current.scrollLeft += scrollValue;
        }
    }, []);
    const goToPreviousSlide = useCallback(() => {
        const scrollValue = getScrollValue(direction, scrollAmount, true);
        if (isMultiSlide) {
            !disableAutoscroll && scroll(scrollValue);
        }
        if (activeSlide === 0) {
            if (isMultiSlide) {
                return;
            }
            if (contentRef.current) {
                contentRef.current.style.transition = 'none';
            }
            setActiveSlide(length - 2);
            setTimeout(() => {
                if (variant === CarouselVariants.Swipe && contentRef.current) {
                    contentRef.current.style.transition = `transform ${Timings.default} ${Easing.easeOut}`;
                }
                setActiveSlide(length - 3);
            }, 50);
        }
        else {
            setActiveSlide(prevSlide => prevSlide - 1);
        }
    }, [direction, scrollAmount, isMultiSlide, activeSlide, disableAutoscroll, scroll, length, variant]);
    const goToNextSlide = useCallback(() => {
        const scrollValue = getScrollValue(direction, scrollAmount, false);
        if (isMultiSlide) {
            !disableAutoscroll && scroll(scrollValue);
        }
        if (activeSlide === length - 1) {
            if (isMultiSlide) {
                setActiveSlide(prevSlide => prevSlide + 1);
                window.scrollTo({
                    left: (activeSlide + 1) * (window.innerWidth / Children.toArray(children).length),
                    behavior: 'smooth',
                });
            }
            if (contentRef.current) {
                contentRef.current.style.transition = 'none';
            }
            setActiveSlide(1);
            setTimeout(() => {
                if (variant === CarouselVariants.Swipe && contentRef.current) {
                    contentRef.current.style.transition = `transform ${Timings.default} ${Easing.easeOut}`;
                }
                setActiveSlide(2);
            }, 50);
        }
        else {
            setActiveSlide(prevSlide => prevSlide + 1);
        }
    }, [direction, scrollAmount, isMultiSlide, activeSlide, length, disableAutoscroll, scroll, children, variant]);
    const handlePaginationChange = (idx) => {
        setActiveSlide(idx);
    };
    useEffect(() => {
        let timer;
        if (autoplay && !isPaused) {
            timer = window.setInterval(() => {
                goToNextSlide();
            }, duration);
        }
        return () => {
            if (timer !== undefined) {
                clearInterval(timer);
            }
        };
    }, [autoplay, duration, goToNextSlide, isPaused, length]);
    const handleTouchStart = useCallback((event) => {
        setTouchStartX(event.touches[0].clientX);
    }, []);
    const handleTouchMove = useCallback((event) => {
        setIsSwiping(true);
        setTouchCurrentX(event.touches[0].clientX);
    }, []);
    const handleTouchEnd = useCallback(() => {
        if (!isSwiping)
            return;
        const swipeThreshold = 50;
        if (touchStartX - touchCurrentX > swipeThreshold) {
            goToNextSlide();
        }
        else if (touchCurrentX - touchStartX > swipeThreshold) {
            goToPreviousSlide();
        }
        setIsSwiping(false);
    }, [goToNextSlide, goToPreviousSlide, isSwiping, touchCurrentX, touchStartX]);
    const handleMouseEvents = useCallback((event) => {
        if (isDesktopDevice) {
            setShowArrow(event.type === 'mouseenter');
        }
    }, []);
    const total = Children.count(children);
    return (jsxs("div", { className: clsx(styles.root, className), ...(!isMultiSlide && { dir: 'ltr' }), "data-testid": dataTestId || 'container', onMouseEnter: handleMouseEvents, onMouseLeave: handleMouseEvents, children: [jsxs("div", { className: styles.contentWrapper, children: [jsx("div", { className: styles.carouselContent, ref: mergedRef, onTouchStart: handleTouchStart, onTouchMove: handleTouchMove, onTouchEnd: handleTouchEnd, "data-testid": "item-container", children: isMerged ? (jsx("div", { className: styles.mergedSlide, children: Children.toArray(children).map((child, index) => (jsx("div", { onClick: () => setDisableAutoscroll(true), children: child }, index.toString()))) })) : Children.toArray(children).slice(-1)
                            .concat(Children.toArray(children))
                            .concat(Children.toArray(children).slice(0, 1))
                            .map((child, index) => ((jsx("div", { className: clsx(styles.slide, { [styles.active]: index === activeSlide }, contentClassName), children: child }, index.toString())))) }), !hidePagination && !isMultiSlide && (jsx(CarouselPagination, { total: total, dotsPlacement: dotsPlacement, activeIndex: activePaginationIndex, onChange: handlePaginationChange }))] }), !hideArrows && (jsxs(Fragment, { children: [!hideLeftArrow && (jsx("div", { className: `${styles.arrowButtonWrap} left`, children: showArrow && (jsx(Button, { centerIcon: IconChevronLeftSmall, skin: ButtonSkinStatusPositive, "data-pulse-name": "carousel-arrow-left", size: arrowSize, className: `${styles.arrowButton} left`, onClick: () => {
                                setDisableAutoscroll(false);
                                goToPreviousSlide();
                                onPrevious && onPrevious();
                            }, "data-testid": "button-left", ...prevButtonProps })) })), !hideRightArrow && (jsx("div", { className: `${styles.arrowButtonWrap} right`, children: showArrow && (jsx(Button, { centerIcon: IconChevronRightSmall, "data-pulse-name": "carousel-arrow-right", className: `${styles.arrowButton} right`, skin: ButtonSkinStatusPositive, size: arrowSize, onClick: () => {
                                setDisableAutoscroll(false);
                                goToNextSlide();
                                onNext && onNext();
                            }, "data-testid": "button-right", ...nextButtonProps })) }))] }))] }));
});
Carousel.displayName = 'Carousel';

export { Carousel };
